import { authoredDocument, authorScriptText } from './authoredSource';
import { describeElement } from './measure';
import type { RuntimeLintContext, RuntimeLintRule } from './runtimeRules';

// Excludes the names SVG shares with HTML — `title`, `desc`, `a`, `image`, `script`, `style`,
// `switch`, `filter` — which appear outside an `<svg>` legitimately.
const SVG_ONLY_TAGS = [
    'g',
    'path',
    'circle',
    'ellipse',
    'rect',
    'line',
    'polyline',
    'polygon',
    'text',
    'tspan',
    'textpath',
    'defs',
    'mask',
    'clippath',
    'lineargradient',
    'radialgradient',
    'stop',
    'use',
    'symbol',
    'marker',
];

// Set by consequence: at 5% the object falls about 12 units short of its box on a 240 canvas —
// roughly one stroke weight — and below that the offset is smaller than the ink it is drawn with.
const ASPECT_TOLERANCE = 0.05;

const svgElementOutsideSvg: RuntimeLintRule = {
    id: 'svg-element-outside-svg',
    severity: 'error',
    guidance:
        'Keep every SVG element inside an `<svg>`. An SVG tag written in HTML — most often a `<g>` wrapped around several `<svg>` elements to share `fill`/`stroke`/`stroke-width` — is not an SVG context: it renders nothing itself and its presentation attributes reach nothing, so the shapes inside fall back to solid black with no stroke. To share paint across several objects, put a class on each `<svg>` and declare the paint in CSS, where it inherits to the children.',
    check(context: RuntimeLintContext): string[] {
        const document = authoredDocument(context);
        const stray = Array.from(document.querySelectorAll(SVG_ONLY_TAGS.join(','))).filter(
            (element) => !element.closest('svg'),
        );
        const seen = new Set<string>();
        const messages: string[] = [];
        for (const element of stray) {
            const tag = element.tagName.toLowerCase();
            const paint = ['fill', 'stroke', 'stroke-width']
                .filter((name) => element.hasAttribute(name))
                .map((name) => `${name}="${element.getAttribute(name)}"`);
            const key = `${tag}|${paint.join(' ')}`;
            if (seen.has(key)) {
                continue;
            }
            seen.add(key);
            const carrying =
                paint.length > 0
                    ? ` It carries ${paint.join(', ')}, which reaches nothing`
                    : ' It renders nothing';
            messages.push(
                `<${tag}> is outside any <svg>, so it is inert HTML rather than an SVG element.` +
                    `${carrying} — shapes inside it fall back to solid black with no stroke. ` +
                    `Move it inside the <svg>, or put the paint on a class on each <svg> so it ` +
                    `inherits to the children.`,
            );
        }
        return messages;
    },
};

// Not `getBoundingClientRect`: the fix changes the size the author declared, and a declared
// length needs no layout pass.
function declaredLength(element: Element, axis: 'width' | 'height'): number | null {
    const attribute = element.getAttribute(axis);
    if (attribute && /^\d+(\.\d+)?(px)?$/.test(attribute.trim())) {
        return Number.parseFloat(attribute);
    }
    const view = element.ownerDocument.defaultView;
    const computed = view?.getComputedStyle(element).getPropertyValue(axis) ?? '';
    const px = /^(\d+(?:\.\d+)?)px$/.exec(computed.trim());
    return px ? Number.parseFloat(px[1]) : null;
}

function viewBoxRatio(element: Element): { width: number; height: number } | null {
    const parts = (element.getAttribute('viewBox') ?? '').trim().split(/[\s,]+/);
    if (parts.length !== 4) {
        return null;
    }
    const width = Number.parseFloat(parts[2]);
    const height = Number.parseFloat(parts[3]);
    return Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0
        ? { width, height }
        : null;
}

const svgViewBoxAspectMismatch: RuntimeLintRule = {
    id: 'svg-viewbox-aspect-mismatch',
    severity: 'error',
    guidance:
        'Give every `<svg>` a `width`/`height` in the same ratio as its `viewBox`. Mismatched, the object does not stretch — `preserveAspectRatio` defaults to `xMidYMid meet`, so it scales to the smaller axis and centres, which means it renders smaller than the box you positioned and sits offset inside it, with the surplus axis becoming dead space that is invisible in the source. To resize an object, change `width` and `height` together rather than redrawing its geometry. Never set `preserveAspectRatio="none"`, which is the one case that really does distort.',
    check(context: RuntimeLintContext): string[] {
        const messages: string[] = [];
        for (const element of Array.from(context.document.querySelectorAll('svg[viewBox]'))) {
            const box = viewBoxRatio(element);
            const width = declaredLength(element, 'width');
            const height = declaredLength(element, 'height');
            if (!box || width === null || height === null || width <= 0 || height <= 0) {
                continue;
            }
            const boxRatio = box.width / box.height;
            const renderedRatio = width / height;
            if (Math.abs(renderedRatio - boxRatio) / boxRatio <= ASPECT_TOLERANCE) {
                continue;
            }
            const label = describeSvg(element);
            if ((element.getAttribute('preserveAspectRatio') ?? '').trim().startsWith('none')) {
                messages.push(
                    `${label} sets preserveAspectRatio="none" with a viewBox of ` +
                        `${box.width}x${box.height} rendered at ${width}x${height}, so every shape ` +
                        `inside is stretched — circles become ellipses and stroke width differs by ` +
                        `axis. Match width/height to the viewBox ratio and drop ` +
                        `preserveAspectRatio.`,
                );
                continue;
            }
            const scale = Math.min(width / box.width, height / box.height);
            const drawnWidth = Math.round(box.width * scale);
            const drawnHeight = Math.round(box.height * scale);
            const deadX = Math.round((width - drawnWidth) / 2);
            const deadY = Math.round((height - drawnHeight) / 2);
            const dead =
                deadX > deadY
                    ? `${deadX}px of dead space on each side`
                    : `${deadY}px of dead space above and below`;
            messages.push(
                `${label} has a viewBox of ${box.width}x${box.height} (${boxRatio.toFixed(2)}:1) ` +
                    `but renders at ${width}x${height} (${renderedRatio.toFixed(2)}:1). It scales ` +
                    `to the smaller axis, so the content draws at ${drawnWidth}x${drawnHeight} ` +
                    `with ${dead} — smaller than the box and offset inside it. Match width/height ` +
                    `to the viewBox ratio.`,
            );
        }
        return messages;
    },
};

function describeSvg(element: Element): string {
    const id = element.getAttribute('id');
    const className = element.getAttribute('class');
    if (id) {
        return `<svg#${id}>`;
    }
    if (className) {
        return `<svg.${className.trim().split(/\s+/).join('.')}>`;
    }
    const label = element.getAttribute('aria-label') ?? element.getAttribute('data-codeo-asset');
    return label ? `<svg> ("${label}")` : '<svg>';
}

interface Segment {
    axis: 'vertical' | 'horizontal';
    fixed: number;
    from: number;
    to: number;
}

// Reads only what it can be certain of: an axis-aligned `<line>`, or a `<path>` holding one
// absolute straight run. Curved, relative and multi-segment paths are left alone rather than
// approximated, so the rule under-reports instead of misfiring.
function segmentOf(element: Element): Segment | null {
    const tag = element.tagName.toLowerCase();
    if (tag === 'line') {
        const [x1, y1, x2, y2] = (['x1', 'y1', 'x2', 'y2'] as const).map((name) =>
            Number.parseFloat(element.getAttribute(name) ?? ''),
        );
        if (![x1, y1, x2, y2].every(Number.isFinite)) {
            return null;
        }
        if (x1 === x2 && y1 !== y2) {
            return { axis: 'vertical', fixed: x1, from: Math.min(y1, y2), to: Math.max(y1, y2) };
        }
        if (y1 === y2 && x1 !== x2) {
            return { axis: 'horizontal', fixed: y1, from: Math.min(x1, x2), to: Math.max(x1, x2) };
        }
        return null;
    }
    if (tag !== 'path') {
        return null;
    }
    const d = (element.getAttribute('d') ?? '').trim();
    const vertical = /^M\s*(-?[\d.]+)[\s,]+(-?[\d.]+)\s*V\s*(-?[\d.]+)\s*Z?$/i.exec(d);
    if (vertical && /V/.test(d)) {
        const x = Number.parseFloat(vertical[1]);
        const y1 = Number.parseFloat(vertical[2]);
        const y2 = Number.parseFloat(vertical[3]);
        return { axis: 'vertical', fixed: x, from: Math.min(y1, y2), to: Math.max(y1, y2) };
    }
    const horizontal = /^M\s*(-?[\d.]+)[\s,]+(-?[\d.]+)\s*H\s*(-?[\d.]+)\s*Z?$/i.exec(d);
    if (horizontal && /H/.test(d)) {
        const y = Number.parseFloat(horizontal[2]);
        const x1 = Number.parseFloat(horizontal[1]);
        const x2 = Number.parseFloat(horizontal[3]);
        return { axis: 'horizontal', fixed: y, from: Math.min(x1, x2), to: Math.max(x1, x2) };
    }
    return null;
}

// `class` counts as paint: computed style is out of reach here (the authored parse has no
// browsing context), and a class is how the skills tell authors to declare stroke and fill.
const PAINT_ATTRS = [
    'class',
    'stroke',
    'stroke-width',
    'opacity',
    'stroke-opacity',
    'style',
] as const;

function samePaint(a: Element, b: Element): boolean {
    return PAINT_ATTRS.every(
        (name) => (a.getAttribute(name) ?? '') === (b.getAttribute(name) ?? ''),
    );
}

// A transform between the element and its `<svg>` puts the segment somewhere its own attributes
// don't describe — two clock hands on identical coordinates, one rotated onto another bearing.
function hasTransformInScope(element: Element): boolean {
    for (let node: Element | null = element; node; node = node.parentElement) {
        if (node.hasAttribute('transform') || node.hasAttribute('transform-origin')) {
            return true;
        }
        if (node.tagName.toLowerCase() === 'svg') {
            break;
        }
    }
    return false;
}

// A tween can separate segments that overlap as authored. Matching a bare substring suppressed a
// quarter of all candidates — `draw` alone hit nine, against scripts merely naming DrawSVGPlugin —
// so a mention only counts when it is shaped like the selector that would target the element.
function maybeTweened(element: Element, script: string): boolean {
    const selectors = [
        ...(element.getAttribute('id') ? [`#${element.getAttribute('id')}`] : []),
        ...(element.getAttribute('class') ?? '')
            .trim()
            .split(/\s+/)
            .filter(Boolean)
            .map((token) => `.${token}`),
    ];
    return selectors.some((selector) =>
        new RegExp(`${selector.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![\\w-])`).test(script),
    );
}

const svgRedundantOverlappingGeometry: RuntimeLintRule = {
    id: 'svg-redundant-overlapping-geometry',
    severity: 'warning',
    guidance:
        "Never draw the same ink twice. Two segments of identical paint that overlap render as one line, so the overlap is invisible — it costs a mark from the object's detail budget and draws nothing. Where two marks are meant to read as one continuous run, draw one segment spanning the whole length.",
    check(context: RuntimeLintContext): string[] {
        const document = authoredDocument(context);
        const script = authorScriptText(document);
        const messages: string[] = [];
        for (const svg of Array.from(document.querySelectorAll('svg'))) {
            const candidates = Array.from(svg.querySelectorAll('line, path'))
                .map((element) => ({ element, segment: segmentOf(element) }))
                .filter(
                    (entry): entry is { element: Element; segment: Segment } =>
                        entry.segment !== null &&
                        !hasTransformInScope(entry.element) &&
                        !maybeTweened(entry.element, script),
                );
            for (let i = 0; i < candidates.length; i += 1) {
                for (let j = i + 1; j < candidates.length; j += 1) {
                    const a = candidates[i];
                    const b = candidates[j];
                    if (a.element.parentElement !== b.element.parentElement) {
                        continue;
                    }
                    if (a.segment.axis !== b.segment.axis || a.segment.fixed !== b.segment.fixed) {
                        continue;
                    }
                    if (!samePaint(a.element, b.element)) {
                        continue;
                    }
                    const overlap =
                        Math.min(a.segment.to, b.segment.to) -
                        Math.max(a.segment.from, b.segment.from);
                    if (overlap <= 0) {
                        continue;
                    }
                    const axisName = a.segment.axis === 'vertical' ? 'x' : 'y';
                    const along = a.segment.axis === 'vertical' ? 'y' : 'x';
                    const union = `${along} ${Math.min(a.segment.from, b.segment.from)}..${Math.max(a.segment.to, b.segment.to)}`;
                    messages.push(
                        `Two ${a.segment.axis} segments at ${axisName}=${a.segment.fixed} in ` +
                            `${describeSvg(svg)} overlap for ${overlap} units of the same paint, ` +
                            `which renders as one line — that much ink draws nothing. Replace them ` +
                            `with a single segment spanning ${union}.`,
                    );
                }
            }
        }
        return messages;
    },
};

const STROKED_TAGS = 'path, circle, ellipse, rect, line, polyline, polygon';

// Inline style first: a `gsap.set` writes there, and it is the value the frame is painted from. The
// presentation attribute is the hand-authored equivalent. Computed style is last and optional —
// it is the only place a class-declared dash lives, and it is the one source a bare parsed document
// cannot answer.
function dashProperty(element: Element, attribute: string): string {
    const inline = (element as SVGElement).style?.getPropertyValue(attribute) ?? '';
    if (inline.trim()) {
        return inline.trim();
    }
    const declared = element.getAttribute(attribute);
    if (declared?.trim()) {
        return declared.trim();
    }
    const view = element.ownerDocument?.defaultView;
    try {
        return view?.getComputedStyle(element).getPropertyValue(attribute).trim() ?? '';
    } catch {
        return '';
    }
}

function dashNumber(element: Element, attribute: string, fallback: number): number {
    const raw = dashProperty(element, attribute);
    const value = Number.parseFloat(raw);
    return Number.isFinite(value) ? value : fallback;
}

// Only the single-value form — `stroke-dasharray: <length>` — is the draw-on idiom. A real dash
// *pattern* (`14 12`) is decoration whose short runs are the point, so it is left alone.
function singleDash(element: Element): number | null {
    const raw = dashProperty(element, 'stroke-dasharray');
    const parts = raw.split(/[\s,]+/).filter(Boolean);
    if (parts.length !== 1) {
        return null;
    }
    const value = Number.parseFloat(parts[0]);
    return Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * How much ink the dash pattern puts at the start of every subpath. The pattern is `dash length`
 * then `gap length`, restarted at each subpath, and the offset slides the path's start along it.
 */
function inkAtSubpathStart(dash: number, offset: number): number {
    const period = dash * 2;
    const into = ((offset % period) + period) % period;
    return into < dash ? dash - into : 0;
}

interface Subpath {
    length: number;
    /** A closed subpath's start and end are the same point, which changes what a boundary paints. */
    closed: boolean;
}

// Dash values read back out of the DOM have been serialized to three decimals — the used value's
// full precision is not reachable from script — so a run computed below the slop of two such
// roundings is indistinguishable from a run of exactly zero.
const BOUNDARY_SLOP = 0.002;

/**
 * Every painted run along a subpath. Checking the start alone is not enough: an offset past the dash
 * clears the start but wraps the pattern round to paint the same sliver at the far end instead,
 * which is exactly as visible. A run that lands on the dash/gap boundary counts only when the
 * subpath is closed — there the two ends meet and the renderer draws the degenerate dash at that
 * seam as a full cap, where on an open subpath it falls off the end and paints nothing.
 */
function inkRuns(dash: number, offset: number, subpath: Subpath): number[] {
    const period = dash * 2;
    const runs: number[] = [];
    for (
        let k = Math.ceil((offset - dash) / period);
        k * period - offset <= subpath.length;
        k += 1
    ) {
        const start = Math.max(0, k * period - offset);
        const end = Math.min(subpath.length, k * period - offset + dash);
        const run = end - start;
        if (run > BOUNDARY_SLOP) {
            runs.push(run);
        } else if (subpath.closed && run >= 0) {
            runs.push(Math.max(run, 0));
        }
    }
    return runs;
}

// Everything but a line and a polyline encloses an area, so its single subpath is closed.
const OPEN_TAGS = new Set(['line', 'polyline']);

/**
 * What can still be judged when the geometry cannot be measured — a parsed document with no layout.
 * The pattern restarts at each `M`, so whatever it paints at a subpath's start it paints once per
 * subpath. Says nothing about the far end of a subpath, which needs the length.
 */
function unmeasuredStartInk(element: Element, dash: number, offset: number): number[][] {
    const ink = inkAtSubpathStart(dash, offset);
    const count =
        element.tagName.toLowerCase() === 'path'
            ? Math.max(subpathFragments(element.getAttribute('d') ?? '').length, 1)
            : 1;
    return new Array(count).fill(null).map(() => (ink > 0 ? [ink] : []));
}

function subpathFragments(d: string): string[] {
    const starts = [...d.matchAll(/[Mm]/g)].map((match) => match.index ?? 0);
    return starts.map((start, index) =>
        d.slice(start, index + 1 < starts.length ? starts[index + 1] : d.length),
    );
}

function measureLength(element: Element): number | null {
    const measure = (element as SVGGeometryElement).getTotalLength;
    if (typeof measure !== 'function') {
        return null;
    }
    const length = measure.call(element as SVGGeometryElement);
    return Number.isFinite(length) && length > 0 ? length : null;
}

/**
 * The length of each subpath, which is what the dash pattern is measured against — it restarts at
 * every `M`. Measured off *prefixes* of the `d` attribute rather than the fragments between the `M`s,
 * because a fragment starting at a relative `m` resolves its coordinates against a different origin
 * and would come out a different shape. Null when the geometry cannot be measured (a parsed document
 * with no layout), leaving the caller to fall back to what it can judge without lengths.
 */
function subpaths(element: Element): Subpath[] | null {
    const tag = element.tagName.toLowerCase();
    if (tag !== 'path') {
        const total = measureLength(element);
        return total === null ? null : [{ length: total, closed: !OPEN_TAGS.has(tag) }];
    }
    const d = element.getAttribute('d') ?? '';
    const fragments = subpathFragments(d);
    if (fragments.length <= 1) {
        const total = measureLength(element);
        return total === null ? null : [{ length: total, closed: /[Zz]/.test(d) }];
    }
    const owner = element.closest('svg');
    if (!owner) {
        return null;
    }
    const probe = element.ownerDocument.createElementNS('http://www.w3.org/2000/svg', 'path');
    owner.appendChild(probe);
    try {
        const measured: Subpath[] = [];
        let consumed = 0;
        let prefix = '';
        for (const fragment of fragments) {
            prefix += fragment;
            probe.setAttribute('d', prefix);
            const total = measureLength(probe);
            if (total === null) {
                return null;
            }
            measured.push({ length: total - consumed, closed: /[Zz]/.test(fragment) });
            consumed = total;
        }
        return measured;
    } finally {
        probe.remove();
    }
}

const dashOffsetLeavesCapDots: RuntimeLintRule = {
    id: 'dash-offset-leaves-cap-dots',
    severity: 'error',
    guidance:
        "A draw-on stroke that is meant to start hidden has to sit inside the dash pattern's *gap*, " +
        'clear of the dash/gap boundary at both ends: `const len = ' +
        'Math.ceil(path.getTotalLength()) + 2; gsap.set(path, { strokeDasharray: len, ' +
        "strokeDashoffset: len + 1 })`. Set the dash to the measured length and the path's ends land " +
        'on that boundary, where the slop between the two values (GSAP writes `strokeDashoffset` ' +
        'rounded to a whole number while `strokeDasharray` keeps its decimals) leaves a fraction of ' +
        'a unit painted — and a dash shorter than the stroke is drawn by a round cap as a ' +
        'full-width dot, one per subpath, sitting on screen for the whole hold before the draw. ' +
        'Offsetting to `length + 1` without padding the dash is not enough: it clears the start of ' +
        'the path and the pattern wraps round to paint the same dot at the end instead.',
    check(context: RuntimeLintContext): string[] {
        const { document, timeline } = context;
        const restoreTo = timeline ? timeline.time() : null;
        if (timeline && restoreTo !== null) {
            timeline.seek(0);
        }
        try {
            const messages: string[] = [];
            for (const element of Array.from(document.querySelectorAll(STROKED_TAGS))) {
                if (!element.closest('svg')) {
                    continue;
                }
                const cap = dashProperty(element, 'stroke-linecap') || 'butt';
                // Butt caps render a sub-unit dash as a hairline nobody sees; round and square ones
                // pad it out to the full stroke width in both directions.
                if (cap === 'butt') {
                    continue;
                }
                const dash = singleDash(element);
                if (dash === null) {
                    continue;
                }
                const width = dashNumber(element, 'stroke-width', 1);
                const offset = dashNumber(element, 'stroke-dashoffset', 0);
                const measured = subpaths(element);
                // A dash far shorter than the run it repeats along is a dotted line, whose short
                // runs are the design. Only a pattern long enough to cover the subpath is a
                // draw-on. The unit of slack is for serialization: a dash set from `getTotalLength`
                // comes back out of the style declaration rounded to three decimals, so a genuine
                // draw-on routinely measures a hair under its own path.
                if (measured?.some((subpath) => dash + 1 < subpath.length)) {
                    continue;
                }
                const perSubpath = measured
                    ? measured.map((subpath) => inkRuns(dash, offset, subpath))
                    : unmeasuredStartInk(element, dash, offset);
                // A subpath carrying a run longer than the stroke is genuinely mid-draw, not dotted.
                if (perSubpath.some((runs) => runs.some((run) => run > width))) {
                    continue;
                }
                const dotted = perSubpath.filter((runs) => runs.length > 0);
                if (dotted.length === 0) {
                    continue;
                }
                const longest = Math.max(...dotted.flat());
                const dots = dotted.length > 1 ? `${dotted.length} dots` : 'a dot';
                messages.push(
                    `${describeElement(element)} paints nothing but ${longest.toFixed(3)} units of ` +
                        `a ${width}-unit ${cap} stroke, so its "hidden" state renders ${dots} ` +
                        `rather than nothing. Pad the dash past the path and park the offset in the ` +
                        `gap: \`const len = Math.ceil(el.getTotalLength()) + 2; gsap.set(el, ` +
                        `{ strokeDasharray: len, strokeDashoffset: len + 1 })\`.`,
                );
            }
            return messages;
        } finally {
            if (timeline && restoreTo !== null) {
                timeline.seek(restoreTo);
            }
        }
    },
};

export const CORE_SVG_LINT_RULES: RuntimeLintRule[] = [
    svgElementOutsideSvg,
    svgViewBoxAspectMismatch,
    svgRedundantOverlappingGeometry,
    dashOffsetLeavesCapDots,
];

export {
    svgElementOutsideSvg,
    svgViewBoxAspectMismatch,
    svgRedundantOverlappingGeometry,
    dashOffsetLeavesCapDots,
};
